import React, { useCallback, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { Text } from '@hub/text'
import useForm, {
  IFormErrors,
  IFormValues,
} from '@scentregroup/shared/hooks/use-form'
import { useLocalUser } from '@scentregroup/shared/hooks/use-local-user'
import useSession from '@scentregroup/shared/hooks/use-session'
import { FormInput } from '@scentregroup/shared/hub-components/form'

import { UpdateCustomerDetails } from '../../graphql/mutations'
import { useSafeMutation } from '../../lib/apollo-client'
import { userAtom } from '../../lib/atoms/user-info'
import { validateEmail } from '../../lib/form'
import { onError } from '../../lib/on-error'
import useNotification from '../../lib/use-notification'
import { useProfileEdit } from '../../lib/use-profile-edit'
import { ProfileSection } from '../../types/profile'
import ProfileLayout from './profile-layout'
import { ERROR_UPDATE_EMAIL } from '../../lib/error-messages'

type Email = {
  email: string
}

type profileEmailErrors = IFormErrors<Email>
type profileEmailValues = IFormValues<Email>

enum ProfileEmailFields {
  EMAIL = 'email',
}

const ProfileEmail: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { openNotification, closeNotification } = useNotification()
  const { renewSession } = useSession()
  const [updateCustomerDetails] = useSafeMutation(UpdateCustomerDetails)
  const { data: user } = useRecoilValue(userAtom)
  const [isSaving, setIsSaving] = useState(false)
  const userLocal = useLocalUser()

  const { cancelEdit } = useProfileEdit(ProfileSection.EMAIL)

  const firstName = user?.firstName
  const lastName = user?.lastName

  let email = ''
  let defaultProfileEmail: { email?: string } = {}

  // If the email in the idToken differs from the one returned by the API, we
  // have hit a race condition after changing email. The email in the idToken
  // should be the most up to date, so use that one instead of the one from the
  // API.
  if (user && userLocal) {
    if (
      userLocal.email &&
      userLocal.emailVerified &&
      userLocal.email !== user.email
    ) {
      email = userLocal.email
    } else {
      email = user.email
    }

    // Don't set the default value until we're sure which value to choose
    defaultProfileEmail = {
      ...defaultProfileEmail,
      email,
    }
  }

  const validateProfileEmail = (
    values: profileEmailValues
  ): profileEmailErrors => {
    const errors: profileEmailErrors = {}
    errors[ProfileEmailFields.EMAIL] = validateEmail(values.email)

    return errors
  }

  const onSave = useCallback(
    async (values: Email) => {
      setIsSaving(true)
      closeNotification()
      try {
        await updateCustomerDetails({
          variables: {
            input: {
              email: values.email,
              // we need to pass the following to comply with the API
              firstName,
              lastName,
            },
          },
        })

        // Skipping refetching the user, as we are relying on the idToken

        // Renewing the session updates the idToken claims, which lets the app
        // know that we are updating email. withRequiresSession will handle the
        // redirect.
        await renewSession()

        cancelEdit()
      } catch (error) {
        onError(error, openNotification, ERROR_UPDATE_EMAIL)
      } finally {
        setIsSaving(false)
      }
    },
    [
      closeNotification,
      updateCustomerDetails,
      firstName,
      lastName,
      renewSession,
      cancelEdit,
      openNotification,
    ]
  )

  const {
    errors,
    values,
    hasChanges,
    resetValues,
    clearErrors,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useForm<Email>(validateProfileEmail, onSave, defaultProfileEmail)

  const onCancel = useCallback(() => {
    closeNotification()
    resetValues([ProfileEmailFields.EMAIL])
    clearErrors()
  }, [closeNotification, resetValues, clearErrors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    clearErrors()
    handleChange({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })
  }

  const onBlur = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void =>
    handleBlur({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })

  return (
    <ProfileLayout
      section={ProfileSection.EMAIL}
      title={'Email address'}
      readOnlyView={<Text>{`${email}`}</Text>}
      handleSubmit={handleSubmit}
      handleCancel={onCancel}
      hasChanges={hasChanges}
      isSubmitting={isSaving}
    >
      <FormInput
        id={'email'}
        name={ProfileEmailFields.EMAIL}
        label={'Email address'}
        value={values.email || ''}
        type={'email'}
        inputMode={'email'}
        autoComplete={'email'}
        autoCorrect={'off'}
        autoCapitalize={'off'}
        isInvalid={!!errors.email}
        errorMessage={errors.email}
        isLoading={false}
        onChange={onChange}
        onBlur={onBlur}
      />
    </ProfileLayout>
  )
}

export default ProfileEmail
